import { useEffect, useState } from "react";
import { Inbox, Check, X, AlertCircle, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Pending tile proposals inbox for the profile owner.
 *
 * Shows BOTH:
 *   - parental: things waiting on the parent's final approval
 *   - clinical: things waiting on a therapist/doctor's clinical sign-off
 *     (only shown when this user is a therapist/doctor on a profile)
 *
 * Approve / reject buttons inline. On parental-approve, the change is
 * applied atomically to the tiles collection. On reject, the proposal
 * is discarded and the tile is unchanged.
 */
function ActionDescription({ proposal }) {
  const action = proposal.action;
  const label = (proposal.payload || {}).label || proposal.target_tile_id || "—";
  if (action === "create") return <>add a new tile <strong>{label}</strong></>;
  if (action === "update") return <>edit the tile <strong>{label}</strong></>;
  if (action === "delete") return <>remove the tile <strong>{label}</strong></>;
  return action;
}

function ProposalCard({ proposal, kind, onDecide }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [error, setError] = useState("");

  const decide = async (decision) => {
    setError("");
    setBusy(true);
    try {
      const path =
        kind === "clinical"
          ? `/proposals/${proposal.id}/clinical-decide`
          : `/proposals/${proposal.id}/parent-decide`;
      await api.post(path, { decision, note: note.trim() || null });
      onDecide();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="brutal-card bg-white p-3"
      data-testid={`proposal-${proposal.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="display text-xs uppercase tracking-[0.2em] font-bold text-[color:var(--coral-deep)]">
            {proposal.profile_name || "Profile"} · {proposal.proposer_role}
          </div>
          <div className="text-sm">
            <strong>{proposal.proposer_name}</strong> wants to{" "}
            <ActionDescription proposal={proposal} />
          </div>
          {proposal.note && (
            <div className="text-xs text-[color:var(--ink-soft)] italic mt-1">
              "{proposal.note}"
            </div>
          )}
          {kind === "parental" && proposal.clinical_decided_at && (
            <div className="text-[11px] text-[color:var(--ink-soft)] mt-1 flex items-center gap-1">
              <ShieldCheck size={11} className="text-[color:var(--coral)]" />
              Clinically approved {new Date(proposal.clinical_decided_at).toLocaleString()}
            </div>
          )}
        </div>
      </div>

      {showNote && (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (visible to the proposer)…"
          maxLength={400}
          rows={2}
          className="brutal-input w-full mb-2 text-sm"
          data-testid={`proposal-${proposal.id}-note`}
        />
      )}

      {error && (
        <div className="text-xs text-[color:var(--coral-deep)] mb-2 flex items-center gap-1">
          <AlertCircle size={12} /> {error}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => decide("approve")}
          disabled={busy}
          className="pill-btn primary text-sm"
          data-testid={`proposal-${proposal.id}-approve`}
        >
          <Check size={12} /> Approve
        </button>
        <button
          type="button"
          onClick={() => decide("reject")}
          disabled={busy}
          className="pill-btn text-sm"
          data-testid={`proposal-${proposal.id}-reject`}
        >
          <X size={12} /> Reject
        </button>
        <button
          type="button"
          onClick={() => setShowNote((v) => !v)}
          className="pill-btn text-sm"
          data-testid={`proposal-${proposal.id}-toggle-note`}
        >
          {showNote ? "Hide note" : "Add note"}
        </button>
      </div>
    </div>
  );
}

export default function PendingProposalsInbox() {
  const [data, setData] = useState({ parental: { items: [] }, clinical: { items: [] } });
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const { data: d } = await api.get("/users/me/pending-proposals");
      setData(d);
    } catch (e) {
      console.error("[PendingProposalsInbox] load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  if (loading) return null;

  const total =
    (data.parental?.items?.length || 0) + (data.clinical?.items?.length || 0);
  if (total === 0) return null;

  return (
    <section
      className="brutal-card bg-[color:var(--gold)] p-4 mb-4"
      data-testid="pending-proposals-inbox"
    >
      <div className="flex items-center gap-2 mb-3">
        <Inbox size={20} />
        <h3 className="display text-lg font-extrabold flex-1">
          {total} change{total === 1 ? "" : "s"} waiting for your approval
        </h3>
      </div>
      <p className="text-sm text-[color:var(--ink-soft)] mb-3">
        A helper or clinician suggested a change to your child's tiles. Review and
        approve or reject — nothing goes live without you.
      </p>

      {data.clinical?.items?.length > 0 && (
        <div className="mb-3">
          <div className="display text-xs uppercase tracking-[0.2em] font-bold mb-2 flex items-center gap-1">
            <ShieldCheck size={12} /> Awaiting your clinical sign-off
          </div>
          <div className="space-y-2">
            {data.clinical.items.map((p) => (
              <ProposalCard key={p.id} proposal={p} kind="clinical" onDecide={reload} />
            ))}
          </div>
        </div>
      )}

      {data.parental?.items?.length > 0 && (
        <div>
          <div className="display text-xs uppercase tracking-[0.2em] font-bold mb-2">
            Awaiting your parental approval
          </div>
          <div className="space-y-2">
            {data.parental.items.map((p) => (
              <ProposalCard key={p.id} proposal={p} kind="parental" onDecide={reload} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
